import { reactMount } from '@/lib/reactMount'
import { useRef, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { fetcher } from '@/lib/fetcher'
import useSWRMutation from 'swr/mutation'
import { SpecialistCard } from '@/components/SpecialistCard'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ErrorMessage } from '@/components/ErrorMessage'
import { CallbackRequestDrawer } from '@/components/CallbackRequestDrawer'

export const findSpecialistWidget = ({ dataset, umbraco, version = 'default' }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current

  const [selectedRegion, setSelectedRegion] = useState('')
  const [filterRegion, setFilterRegion] = useState('')
  const [callbackDrawerOpen, setCallbackDrawerOpen] = useState(false)
  const [selectedSpecialist, setSelectedSpecialist] = useState(null)

  const {
    trigger: triggerSearch,
    data: searchResults,
    error,
    isMutating: isLoading
  } = useSWRMutation('/api/specialist/find', fetcher)

  useEffect(() => {
    triggerSearch({ method: 'GET' })
  }, [triggerSearch])

  const specialists = searchResults?.data || []

  // Compute region list from Umbraco Locations (array of strings), sorted A→Z
  const regionOptions = useMemo(() => {
    const locations = Array.isArray(umbracoRef.current?.Locations) ? umbracoRef.current.Locations : []
    return locations.filter(Boolean).slice().sort((a, b) => a.localeCompare(b))
  }, [])

  const filteredSpecialists = useMemo(() => {
    if (!filterRegion) return []
    // Support both Regions (array) and region (string) for backward compatibility
    return specialists.filter(specialist =>
      (Array.isArray(specialist.Regions) && specialist.Regions.includes(filterRegion)) ||
      specialist.region === filterRegion
    )
  }, [filterRegion, specialists])

  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value)
  }

  const handleFindNow = () => {
    setFilterRegion(selectedRegion)
  }

  const handleRequestCallback = (specialist) => {
    setSelectedSpecialist(specialist)
    setCallbackDrawerOpen(true)
  }

  return (
    <>
      <section>
        <header className='_block-section relative mx-auto py-10 bg-primary-950'>
          <form className='relative max-w-7xl mx-auto overflow-hidden flex flex-col lg:flex-row lg:items-center gap-3 px-4 sm:px-6 py-8 lg:px-8 lg:py-16 '>
            <div className='text-default-50 pb-4 lg:pb-0 lg:pr-8 max-w-[340px]'>
              <h2 className='relative z-10 text-3xl xl:text-3xl leading-7 font-extrabold lg:w-full mb-3'>Find a specialist</h2>
              <p className='text-base font-semibold'>We’ll put you in touch with a Relationship Manager in that area</p>
            </div>

            <div className='relative z-10 flex flex-col md:flex-row w-full gap-4'>
              <div className='grid grid-cols-1 grow'>
                <FormField
                  type='select'
                  label=''
                  id='selectedRegion'
                  name='selectedRegion'
                  value={selectedRegion}
                  onChange={handleRegionChange}
                  options={regionOptions}
                  placeholder='Select a region'
                  disabled={isLoading}
                  className='relative grow _lg-select'
                />
              </div>
              <Button
                size='lg'
                variant='primary'
                className='relative'
                onClick={handleFindNow}
                disabled={!selectedRegion}
              >
                Find Now
              </Button>
            </div>
          </form>
          <div aria-hidden='true' className='absolute inset-0 transform-gpu overflow-hidden'>
            <div className='absolute border-radius-[800px] left-1/2 -bottom-[540px] md:-bottom-[520px] lg:-bottom-[440px] w-[1000px] h-[800px] bg-blue-rad -translate-x-1/2 blur-2xl opacity-70 pointer-events-none' />
          </div>
        </header>

        {
          filteredSpecialists.length > 0 && (
            <div className='mx-auto max-w-[1060px] px-4 xl:px-3 space-y-16 mt-16 mb-16'>
              <div className='mx-auto space-y-8'>
                {isLoading && <LoadingSpinner message='Loading specialists...' />}

                {!isLoading && !error && filteredSpecialists.length > 0 && (
                  <div className='grid grid-cols-1 gap-x-8 gap-y-10 md:gap-y-16 sm:grid-cols-2 lg:grid-cols-3'>
                    {filteredSpecialists.map((specialist, idx) => (
                      <SpecialistCard
                        key={`${specialist.name}-${specialist.phone || ''}-${idx}`}
                        specialist={specialist}
                        onRequestCallback={handleRequestCallback}
                      />
                    ))}
                  </div>
                )}

                {!isLoading && !error && filterRegion && filteredSpecialists.length === 0 && specialists.length > 0 && (
                  <div className='text-center'>
                    <p className='text-gray-500 text-lg'>
                      No Relationship Manager available for this location.
                    </p>
                  </div>
                )}

                {!isLoading && error && (
                  <ErrorMessage message='Failed to load specialists. Please refresh the page.' />
                )}
              </div>
            </div>
          )
}
      </section>

      <CallbackRequestDrawer
        open={callbackDrawerOpen}
        onClose={() => setCallbackDrawerOpen(false)}
        specialist={selectedSpecialist}
        regionOptions={regionOptions}
      />
    </>
  )
}

reactMount('findSpecialistWidget', findSpecialistWidget)
